import { partition } from 'es-toolkit';
import React, { SyntheticEvent, useCallback, useEffect, useRef } from 'react';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { AlertColor, Box, darken, lighten, SvgIconProps, Typography } from '@mui/material';
import TouchRipple, { TouchRippleActions, TouchRippleProps } from '@mui/material/ButtonBase/TouchRipple';

import { usePrevious } from '@paragon/client-tools';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import {
  getNotificationCenterRelevantSnackbars,
  sortSnackbarsBySeverity,
  toMuiSeverity,
} from '../../../store/selectors/notificationSelectors';
import { leftSidebarSet } from '../../../store/slices/panelDataSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';

/**
 * I believe `TouchRipple` is typed incorrectly.
 */
const TouchRippleTyped = TouchRipple as React.ForwardRefExoticComponent<
  React.PropsWithoutRef<TouchRippleProps> & React.RefAttributes<TouchRippleActions>
>;

function VariantIcon({ variant, ...iconProps }: { variant: AlertColor } & SvgIconProps) {
  switch (variant) {
    case 'error':
      return <ErrorIcon {...iconProps} />;

    case 'warning':
      return <WarningIcon {...iconProps} />;

    case 'info':
      return <InfoIcon {...iconProps} />;

    case 'success':
      return <CheckBoxIcon {...iconProps} />;

    default:
      assertExhaustiveSwitch(variant);
  }
}

function variantToUserVisibleString(variant: AlertColor, quantity: number) {
  switch (variant) {
    case 'error':
      return quantity > 1 ? 'errors' : 'error';

    case 'warning':
      return quantity > 1 ? 'warnings' : 'warning';

    case 'info':
    case 'success':
      return quantity > 1 ? 'notifications' : 'notification';

    default:
      assertExhaustiveSwitch(variant);
  }
}

export function StatusBarNotifier() {
  const dispatch = useAppDispatch();

  const rippleTargetRef = useRef<HTMLElement | null>(null);
  const rippleEffectRef = useRef<TouchRippleActions | null>(null);

  const triggerRippleEffect = useCallback(() => {
    if (rippleTargetRef.current == null || rippleEffectRef.current == null) return;

    const rect = rippleTargetRef.current.getBoundingClientRect();

    try {
      rippleEffectRef.current.start(
        { clientX: rect.left + rect.width / 2, clientY: rect.top + rect.height / 2 } as unknown as SyntheticEvent,
        { center: false },
      );
    } catch (error) {
      // some would say providing a fake event to an internal MUI component is risky so prevent a whitescreen crash ;)
      console.error('Failed to start ripple effect for notification', error);
    }

    setTimeout(() => {
      try {
        rippleEffectRef.current?.stop({} as unknown as SyntheticEvent);
      } catch (error) {
        console.error('Failed to stop ripple effect for notification', error);
      }
    }, 320);
  }, []);

  const snackbars = useAppSelector(getNotificationCenterRelevantSnackbars);
  const previousSnackbars = usePrevious(snackbars);

  useEffect(() => {
    if (snackbars.length > (previousSnackbars?.length ?? 0)) {
      triggerRippleEffect();
    }
  }, [previousSnackbars?.length, snackbars.length, triggerRippleEffect]);

  const notificationsPanelOpen = useAppSelector((state) => state.panelData.leftSidebar === 'notifications');

  if (snackbars.length === 0) {
    return (
      <Box sx={{ padding: 1 }}>
        <Typography variant="body2" color="textSecondary">
          No notifications
        </Typography>
      </Box>
    );
  }

  const orderedSnackbars = sortSnackbarsBySeverity(snackbars);
  const worstVariant = toMuiSeverity(orderedSnackbars[0][1].variant);

  const [worstNotifications, otherNotifications] = partition(
    orderedSnackbars,
    ([, options]) => options.variant === worstVariant,
  );

  const messages = [
    `${worstNotifications.length} ${variantToUserVisibleString(worstVariant, worstNotifications.length)}`,
    otherNotifications.length > 0 &&
      `${otherNotifications.length} other ${variantToUserVisibleString('info', otherNotifications.length)}`,
  ];

  return (
    <Box
      ref={rippleTargetRef}
      key="multiple-notifications"
      sx={{
        padding: 1,
        cursor: 'pointer',
      }}
      onClick={() => {
        dispatch(leftSidebarSet(notificationsPanelOpen ? 'open' : 'notifications'));
      }}
    >
      <Typography
        variant="body2"
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',

          // mimic color behavior of MUI Alert w/ variant="outlined"
          color: (theme) => (theme.palette.mode === 'light' ? darken : lighten)(theme.palette[worstVariant].light, 0.6),
        }}
      >
        <VariantIcon color="inherit" fontSize="inherit" variant={worstVariant} />
        {messages.filter((message) => message).join('; ') +
          ` (click to ${notificationsPanelOpen ? 'hide' : 'show'} notification center)`}
      </Typography>

      <TouchRippleTyped ref={rippleEffectRef} center />
    </Box>
  );
}
